// hooks/useAuthSync.js - v3.0 Multi-Tab Auth Sync Hook

import { useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import authSyncService from '../services/authSyncService';
import { AuthContext } from '../context/AuthContext';

/**
 * Hook for multi-tab authentication synchronization
 * Automatically syncs login/logout/token refresh across browser tabs
 *
 * @returns {Object} { isSupported, broadcast methods }
 */
export const useAuthSync = () => {
  const navigate = useNavigate();
  const { login, logout, updateToken } = useContext(AuthContext);

  useEffect(() => {
    // Only setup if BroadcastChannel is supported
    if (!authSyncService.isSupported()) {
      console.warn('⚠️  Multi-tab sync not supported in this browser');
      return;
    }

    // Subscribe to sync events
    const unsubscribe = authSyncService.subscribe((type, payload) => {
      switch (type) {
        case 'LOGIN':
          // Another tab logged in - sync the login state
          if (payload.user && payload.accessToken) {
            login(payload.user, payload.accessToken);
            // Optional: Navigate to dashboard if on login page
            if (window.location.pathname === '/login') {
              navigate('/dashboard');
            }
          }
          break;

        case 'LOGOUT':
          // Another tab logged out - sync the logout (applied, not re-broadcast: no echo)
          logout({ broadcast: false });
          // Navigate to login page
          navigate('/login');
          break;

        case 'TOKEN_REFRESH':
          // Another tab refreshed token - update our token
          if (payload.accessToken) {
            updateToken(payload.accessToken, payload.expiresIn, { broadcast: false });
          }
          break;

        case 'SESSION_EXPIRED':
          // Session expired in another tab - logout
          logout();
          navigate('/login', {
            state: {
              message: 'جلستك انتهت، يرجى تسجيل الدخول مرة أخرى',
              messageEn: 'Your session has expired, please login again'
            }
          });
          break;

        case 'STATUS_CHANGE':
          // Account status changed (suspended, etc.)
          if (payload.status === 'suspended' || payload.status === 'archived') {
            logout();
            navigate('/login', {
              state: {
                message: payload.reason || 'تم تعليق حسابك',
                messageEn: 'Your account has been suspended'
              }
            });
          }
          break;

        case 'IMPERSONATION_START':
          // Admin started impersonating - sync the impersonation state
          if (payload.targetUser && payload.impersonationToken) {
            login(payload.targetUser, payload.impersonationToken);
            navigate('/dashboard');
          }
          break;

        case 'IMPERSONATION_END':
          // Impersonation ended - logout
          logout();
          navigate('/login');
          break;

        default:
      }
    });

    // Cleanup on unmount
    return () => {
      unsubscribe();
    };
  }, [login, logout, updateToken, navigate]);

  return {
    isSupported: authSyncService.isSupported(),
    broadcastLogin: authSyncService.broadcastLogin.bind(authSyncService),
    broadcastLogout: authSyncService.broadcastLogout.bind(authSyncService),
    broadcastTokenRefresh: authSyncService.broadcastTokenRefresh.bind(authSyncService),
    broadcastSessionExpired: authSyncService.broadcastSessionExpired.bind(authSyncService),
    broadcastStatusChange: authSyncService.broadcastStatusChange.bind(authSyncService),
    broadcastImpersonationStart: authSyncService.broadcastImpersonationStart.bind(authSyncService),
    broadcastImpersonationEnd: authSyncService.broadcastImpersonationEnd.bind(authSyncService)
  };
};

export default useAuthSync;
